// @flow

import * as React from 'react';

import { Link } from 'react-router-dom';

import type { Currency } from '@gandi/billing';
import { NOTIFICATION_ID as CART_NOTIFICATION_ID } from '@gandi/cart';
import { notifyDanger, closeNotification } from '@gandi/notifications';
import type { TranslatorProps } from '@gandi/translate';
import { Cell, Text, Base, Fqdn, PopoverContainer, Popover } from '@gandi/ui';
import { ListRow, ListItem, Action } from '@gandi/ui/deprecated';

import CorporatePopOver from '../../../DomainCreate/components/DomainList/FqdnCell/CorporatePopOver';

import type { Dispatch } from '../../../types';
import { actions } from '../../modules';

import type { ResultItem } from '../../types';

import CellCol from './CellCol';
import CellRowGroup from './CellRowGroup';
import CellRowToCol from './CellRowToCol';

import styles from './DomainList.scss';

const { addToCart, removeFromCart } = actions;

type Props = {
  dispatch: Dispatch,
  translator: TranslatorProps,
  domain: ResultItem,
  corporatePackMonthlyPrice: number | void,
  currency: Currency,
  verbose: boolean,
  taxesIncluded: boolean,
};

function getChangeTranslation(
  __: TranslatorProps['__'],
  change: string,
  duration: number,
) {
  if (change === 'unchanged') {
    return __("Expiration date won't change");
  }

  if (change === 'renew') {
    return __('Will be renewed for %(count)s years', { count: duration });
  }

  if (change === 'reset') {
    return __('Will expire in %(count)s years', { count: duration });
  }

  return '';
}

export default class DomainRow extends React.PureComponent<Props> {
  /**
   * Handle the addition of the suggestion in the cart.
   */
  onClickItem: (evt: SyntheticMouseEvent<>) => any = (
    evt: SyntheticMouseEvent<>,
  ) => {
    const { translator, dispatch, domain } = this.props;

    evt.preventDefault();

    if (domain.addedToCart) {
      return dispatch(removeFromCart(domain)).catch(() => {
        dispatch(
          notifyDanger(translator.__('An error occurred with your product.')),
        );
      });
    }

    return dispatch(addToCart(domain)).catch(() => {
      dispatch(closeNotification(CART_NOTIFICATION_ID));
      dispatch(
        notifyDanger(translator.__('An error occurred with your product.')),
      );
    });
  };

  /**
   * Return the icon of the row.
   */
  getIcon(): string {
    const { domain } = this.props;

    if (domain.isWorking) return '';
    if (domain.addedToCart) return 'checkmark';
    if (domain.isTransferable) return 'cart';

    return '';
  }

  renderMainCell(): React.Node {
    const {
      translator: { __ },
      verbose,
      currency,
      corporatePackMonthlyPrice,
      domain: {
        isTransferable,
        corporate,
        invalid,
        isWorking,
        availability,
        fqdn,
      },
    } = this.props;

    const popoverIcon =
      isTransferable && corporate ? (
        <CorporatePopOver
          price={corporatePackMonthlyPrice}
          id={`${fqdn}-corporate-popover`}
          currency={currency}
        />
      ) : undefined;

    const renderedFQDN = (
      <Fqdn maxLength={30} className={styles.fqdn}>
        {fqdn}
      </Fqdn>
    );

    // eslint-disable-next-line testing-library/render-result-naming-convention
    let content: React.MixedElement = renderedFQDN;

    if (verbose && !popoverIcon && isTransferable) {
      content = (
        <CellRowToCol
          dangerouslySetInnerHTML={__('%(fqdn)s can be transferred to Gandi.', {
            fqdn: renderedFQDN,
            withHTML: true,
            escapeParameters: false,
          })}
        />
      );
    }

    if (!isTransferable && !isWorking && !invalid) {
      content = (
        <CellRowToCol
          dangerouslySetInnerHTML={__(
            '%(fqdn)s cannot be transferred to Gandi.',
            {
              fqdn: renderedFQDN,
              withHTML: true,
              escapeParameters: false,
            },
          )}
        />
      );
    }

    if (
      !isTransferable &&
      !isWorking &&
      !invalid &&
      availability === 'available'
    ) {
      content = (
        <Base flex flexGrow1 flexJustifyBetween>
          <span
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={__(
              '%(fqdn)s is available for registration.',
              {
                fqdn: renderedFQDN,
                withHTML: true,
                escapeParameters: false,
              },
            )}
          />
          <Link to={`/domain/suggest?search=${fqdn}`}>{__('Register')}</Link>
        </Base>
      );
    }

    if (!isTransferable && !isWorking && invalid) {
      content = (
        <CellRowToCol
          dangerouslySetInnerHTML={__('%(fqdn)s is not a valid domain', {
            fqdn: renderedFQDN,
            withHTML: true,
            escapeParameters: false,
          })}
        />
      );
    }

    return (
      <Cell flexGrow1 popoverIcon={popoverIcon}>
        {content}
      </Cell>
    );
  }

  renderPriceCell(): null | React.Node {
    const {
      translator: { __, formatPrice },
      domain: { price },
      taxesIncluded,
    } = this.props;

    if (!price) {
      return null;
    }

    const duration = price.duration || 1;

    const amount =
      (taxesIncluded ? price.amount.afterTaxes : price.amount.beforeTaxes) *
      duration;

    return (
      <Cell>
        <CellCol>
          <Text>{formatPrice(amount, price.currency)}</Text>
          <Text color="light" size="small">
            {getChangeTranslation(__, price.change, duration)}
          </Text>
        </CellCol>
      </Cell>
    );
  }

  renderAvailabilityMessage(): React.Node {
    const {
      translator: { __ },
      domain: { fqdn, availabilityMsg },
    } = this.props;
    return (
      <Cell tagName="div" align="end">
        <PopoverContainer
          id={`transfer-${fqdn}`}
          placement="bottom"
          renderReference={getReferenceProps => (
            <Text tagName="a" href="#" role="button" {...getReferenceProps()}>
              {__('Why?')}
            </Text>
          )}
        >
          {getTooltipProps => (
            <Popover title="" {...getTooltipProps()}>
              {availabilityMsg}
            </Popover>
          )}
        </PopoverContainer>
      </Cell>
    );
  }

  renderSuggestLink(): React.Node {
    const {
      translator: { __ },
      domain: { fqdn: term },
    } = this.props;
    return (
      <Cell align="end">
        <Link to={`/domain/suggest?search=${term}`}>
          {__('Find domains similar to %(term)s', { term })}
        </Link>
      </Cell>
    );
  }

  render(): React.Node {
    const {
      translator: { __ },
      domain: {
        fqdn,
        isWorking,
        invalid,
        isTransferable,
        availability,
        availabilityMsg,
        addedToCart,
        isBeingAddedToCart,
        isBeingRemovedFromCart,
      },
    } = this.props;

    const isActionnable =
      !isWorking &&
      !!isTransferable &&
      !isBeingAddedToCart &&
      !isBeingRemovedFromCart;

    const actionTitle = addedToCart
      ? __('Remove from cart')
      : __('Add to cart');

    const actionTheme = !addedToCart ? 'green' : 'success-plain';
    const actionLoading =
      isWorking || isBeingAddedToCart || isBeingRemovedFromCart;

    const shouldDisplayMessage =
      !isWorking &&
      !invalid &&
      !isTransferable &&
      !!availabilityMsg &&
      availability !== 'available';

    const isInvalid = !isWorking && !isTransferable && invalid;

    return (
      <ListRow id={fqdn} separatorBottom>
        <ListItem>
          <CellRowGroup className={styles.flexGrow1}>
            {this.renderMainCell()}
            {this.renderPriceCell()}
          </CellRowGroup>
          {shouldDisplayMessage && this.renderAvailabilityMessage()}
          {isInvalid && this.renderSuggestLink()}
        </ListItem>
        {(isTransferable || isWorking) && (
          <Action
            icon={this.getIcon()}
            theme={actionTheme}
            loading={actionLoading}
            onClick={isActionnable ? this.onClickItem : undefined}
            disabled={!isActionnable}
            title={actionTitle}
          >
            {actionTitle}
          </Action>
        )}
      </ListRow>
    );
  }
}
